import { resultColors } from '@/helpers/global-helpers'
import {
  getTrendDataForAverages,
  getTrendTextForStatItem
} from '@/helpers/trend-helpers'
import { processAmounts } from '@/helpers/results-helper'
import { getLabelsForQuestion } from '@/helpers/question-helpers'

// LINE CHART
export const getLineChartDataForStatItems = ({
  statItems,
  statItemsTrend,
  hasLinearRegression,
  shouldFormatZeroToHundred = false,
  isEnps = false,
  isParticipation = false,
  isQuestion = false,
  tr,
  selectedAttributeSegment
}) => {
  let min = 100 // used later for domain and ticks
  let max = -100 // in charts, default min/max values are enps
  const isSegment =
    selectedAttributeSegment && selectedAttributeSegment.name !== 'all'
  const resultObj = Object.keys(statItems).reduce(
    (datesForStats, statItemName) => {
      const statItem = {
        ...statItems[statItemName],
        ...(statItemsTrend?.[statItemName] || {}),
        name: statItemName
      }
      const averages = (statItem.averages || []).map((a) => {
        return {
          ...a,
          value: processHistoricalValue({
            statItem,
            value: a.value
          })
        }
      })
      averages
        .filter((a) => a.value !== undefined)
        .forEach((a, i) => {
          const date = new Date(a.date_modified)
          const dateString = date.toISOString()

          if (!datesForStats[dateString]) {
            datesForStats[dateString] = { date }
          }
          if (!datesForStats[dateString][statItem.name]) {
            datesForStats[dateString][statItem.name] = {}
          }

          if (
            (isParticipation || (shouldFormatZeroToHundred && !isQuestion)) &&
            !isSegment
          ) {
            a.value = a.value * 10
          }
          const numberOfDecimalsToRoundTo =
            shouldFormatZeroToHundred || isSegment ? 0 : 1
          let value = (Math.round(a.value * 10) / 10).toFixed(
            numberOfDecimalsToRoundTo
          )

          if (a.value < min) {
            min = a.value
          }
          if (a.value > max) {
            max = a.value
          }

          datesForStats[dateString][statItem.name].value = value
          if (hasLinearRegression && !isSegment) {
            const numberOfDecimalsToRoundToForTrend = 3
            const roundedRegressionValue = (
              Math.round(statItem.regressionValues[i] * 1000) / 1000
            ).toFixed(numberOfDecimalsToRoundToForTrend)
            let valueForRegression =
              isParticipation || shouldFormatZeroToHundred
                ? roundedRegressionValue * 10
                : roundedRegressionValue

            const linearRegressionValue = processHistoricalValue({
              statItem,
              value: valueForRegression
            })
            datesForStats[dateString].linearRegressionValue = {
              value: linearRegressionValue
            }
          }
        })
      return datesForStats
    },
    {}
  )

  const options = getOptionsObjectForLineChart({
    min,
    max,
    shouldFormatZeroToHundred,
    isQuestion,
    isParticipation,
    isEnps,
    isSegment
  })

  const series = getLineChartSeriesForStatItems({
    statItems,
    hasLinearRegression,
    tr
  })

  return {
    data: Object.values(resultObj)
      .map((r) => r)
      .sort((a, b) => a.date - b.date),
    options,
    series
  }
}

const processHistoricalValue = ({ statItem, value }) => {
  let val = value
  if (statItem.type === 'yesOrNo') {
    val = val * 10
  }
  return val
}

const getOptionsObjectForLineChart = ({
  min,
  max,
  shouldFormatZeroToHundred = false,
  isParticipation = false,
  isQuestion = false,
  isEnps = false,
  isSegment = false
}) => {
  min = Math.floor(min)
  max = Math.ceil(max)

  const options = { domain: [min, max], ticks: 2 }
  if (isEnps) {
    options.domain[0] = min - 10
    options.domain[1] = 100
    options.ticks = 5
    options.fractionalDigits = 0
  } else {
    if (shouldFormatZeroToHundred && !isSegment) {
      // TODO: domain min?
      let zeroToHundredMax = 100
      if (max < 10) {
        zeroToHundredMax = 10
      } else if (max < 50) {
        zeroToHundredMax = 50
      }
      options.domain = [0, zeroToHundredMax]
      options.ticks = 5
      options.fractionalDigits = 0
    } else if (!isSegment) {
      if (max >= 8) {
        options.domain[1] = 10
      }
      if (min <= 7) {
        options.domain[0] = 5
      }
      if (min <= 4) {
        options.domain[0] = 0
      }
      options.ticks = 10 - options.domain[0] - 1
    }
  }
  if (isParticipation && !isSegment) {
    options.percentage = true
    options.percentageItem = 'participation'
  }
  if (isParticipation || isQuestion) {
    // when we show history for participation its always always a very small graph
    options.largeFontSize = true
  }
  return options
}

export const getLineChartSeriesForStatItems = ({
  statItems,
  hasLinearRegression,
  tr
}) => {
  if (hasLinearRegression) {
    const statItemName = Object.keys(statItems)[0]
    return [
      { name: 'linearRegressionValue', title: 'linearRegressionValue' },
      {
        name: statItemName,
        title: tr(statItems[statItemName].messages, 'title')
      }
    ]
  }
  return Object.keys(statItems).map((statItemName) => {
    return {
      name: statItemName,
      title: tr(statItems[statItemName].messages, 'title')
    }
  })
}

// BAR CHART
export const getBarChartDataForStatItem = ({ statItem, t }) => {
  const result = []
  if (statItem.type === 'tag' || statItem.type === 'category') {
    processAmounts(result, statItem, (target, i, amount) => {
      target.push({ name: i, value: amount })
    })
  } else {
    // we assume question if not tag or category
    const labels = getLabelsForQuestion({
      t,
      type: statItem.type,
      rangeKeys: statItem.rangeKeys,
      yesIsBad: statItem.yesIsBad
    })
    if (statItem.type === 'yesOrNo') {
      // Handle yesOrNo type with only amount_0 and amount_10
      result.push({ name: labels[0], value: statItem.amount_0 || 0 })
      result.push({ name: labels[1], value: statItem.amount_10 || 0 })
    } else if (statItem.rangeKeys) {
      statItem.rangeKeys.forEach((rangeKey, i) => {
        const amountKey = `amount_${rangeKey}`
        const amount = statItem[amountKey] || 0
        result.push({ name: labels[i], value: amount })
      })
    } else {
      processAmounts(result, statItem, (target, i, amount) => {
        target.push({ name: labels[i], value: amount })
      })
    }
  }
  return result
}

// DONUT CHART
export const getDonutDataForStatItem = ({
  statItem,
  title,
  description,
  theme = 'default',
  chartType = 'tag',
  shouldBeZeroToHundred = false,
  isPercentage = false,
  resultsPerPerson,
  localeNumber,
  name,
  t
}) => {
  const totalPeople = Object.keys(resultsPerPerson || {}).reduce(
    (total, colorValue) => {
      total = total + resultsPerPerson[colorValue]
      return total
    },
    0
  )
  let currentValue = null
  if (chartType === 'enps') {
    currentValue = statItem?.npsData?.score
  } else if (resultsPerPerson) {
    currentValue = resultsPerPerson[statItem.resultName]
  } else {
    if (statItem.value) {
      currentValue = shouldBeZeroToHundred
        ? statItem.value * 10
        : statItem.value
    }
  }

  let currentValueTitle = '-'
  if (chartType === 'enps') {
    currentValueTitle = currentValue
  } else if (resultsPerPerson) {
    currentValueTitle =
      localeNumber(
        (resultsPerPerson[statItem.resultName] / totalPeople) * 100,
        0
      ) + '%'
  } else {
    if (statItem.value) {
      if (isPercentage) {
        currentValueTitle = localeNumber(statItem.value * 10, 0, 'tag') + '%'
      } else if (shouldBeZeroToHundred) {
        currentValueTitle = localeNumber(statItem.value * 10, 0, 'tag')
      } else {
        const value = localeNumber(statItem.value, 1, 'tag')
        currentValueTitle =
          value === 'noData' ? t('widgetTagsDonuts.centerValue.noData') : value
      }
    }
  }

  const chartData = getDonutChartDataForValue({
    value: resultsPerPerson
      ? (resultsPerPerson[statItem.resultName] / totalPeople) * 10
      : statItem.value,
    npsData: statItem.npsData,
    chartType,
    theme,
    name,
    t,
    result: statItem.resultName
  })
  const trendData = getTrendDataForAverages({
    averages: statItem.averages,
    regressionValues: statItem.regressionValues
  })

  const trendText = getTrendTextForStatItem({
    statItem,
    title,
    chartType,
    t
  })

  const hasTrend =
    statItem.regressionValues &&
    statItem.regressionValues.length > 1 &&
    trendData?.trend !== 'noTrend'

  return {
    tagTitle: title,
    tagDescription: description,
    theme,
    chartData,
    currentValue,
    currentValueTitle,
    trend: {
      hasTrend: hasTrend,
      direction: trendData?.trend,
      tooltip: trendText
    }
  }
}

export const getDonutChartDataForValue = ({
  value,
  npsData = null,
  chartType = 'tag',
  shouldBeZeroTohundred,
  theme = 'default',
  name = 'unknownname',
  t,
  result = null // to decide color for results-per-person
}) => {
  if (chartType === 'enps') {
    if (npsData) {
      return [
        {
          name: 'promoters',
          title: t('widgetTagsDonuts.tooltip.nps_promoters'),
          value: npsData.promoters,
          tooltip: `<strong>${t(
            'widgetTagsDonuts.tooltip.nps_promoters'
          )}:</strong> ${npsData.promoters} (${Math.round(
            npsData.promotersPercentage * 100
          )}%)`,
          color:
            theme === 'monochrome'
              ? 'var(--kt-primary)'
              : colorFromEnpsType('promoters')
        },
        {
          name: 'passives',
          title: t('widgetTagsDonuts.tooltip.nps_passives'),
          value: npsData.passives,
          tooltip: `<strong>${t(
            'widgetTagsDonuts.tooltip.nps_passives'
          )}:</strong> ${npsData.passives} (${Math.round(
            npsData.passivesPercentage * 100
          )}%)`,
          color:
            theme === 'monochrome'
              ? 'var(--kt-primary)'
              : colorFromEnpsType('passives')
        },
        {
          name: 'detractors',
          title: t('widgetTagsDonuts.tooltip.nps_detractors'),
          value: npsData.detractors,
          tooltip: `<strong>${t(
            'widgetTagsDonuts.tooltip.nps_detractors'
          )}:</strong> ${npsData.detractors} (${Math.round(
            npsData.detractorsPercentage * 100
          )}%)`,
          color:
            theme === 'monochrome'
              ? 'var(--kt-gray-300)'
              : colorFromEnpsType('detractors')
        }
      ]
    } else {
      return []
    }
  } else {
    const max = shouldBeZeroTohundred ? 100 : 10
    if (value) {
      let color =
        theme === 'monochrome'
          ? resultColors.brand
          : valueColorForQuestion({ value })
      if (result) {
        color = colorFromResult(result)
      }
      return [
        {
          name: `${name}-value`,
          title: 'valuesegment',
          value,
          tooltip: null,
          color
        },
        {
          name: 'unfilled',
          title: 'unfilled',
          value: max - value,
          tooltip: null,
          color: 'var(--kt-gray-300)'
        }
      ]
    } else {
      return []
    }
  }
}

export const colorFromEnpsType = (type) => {
  if (type === 'promoters') {
    return resultColors.green
  } else if (type === 'passives') {
    return resultColors.yellow
  } else {
    return resultColors.red
  }
}
export const colorFromResult = (result) => {
  if (result === 'high') {
    return resultColors.green
  } else if (result === 'medium') {
    return resultColors.yellow
  } else {
    return resultColors.red
  }
}

export const valueColorForQuestion = (
  question,
  individualYesOrNoValue = null
) => {
  if (question.theme === 'monochrome') {
    return 'var(--kt-primary-light)'
  }
  if (question.type === 'yesOrNo' && individualYesOrNoValue) {
    if (question.yesIsBad) {
      return individualYesOrNoValue === 'yes'
        ? resultColors.red
        : resultColors.green
    } else {
      return individualYesOrNoValue === 'yes'
        ? resultColors.green
        : resultColors.red
    }
  }
  const value = question.value
  if (typeof value === 'number') {
    if (value < 4) {
      return resultColors.red
    } else if (value >= 7) {
      return resultColors.green
    } else {
      return resultColors.yellow
    }
  } else {
    return ''
  }
}
